<template>
  <div class="excel-upload-container">
    <!-- File Upload -->
    <div class="upload-section">
      <input 
        type="file" 
        @change="handleFileUpload" 
        accept=".xlsx, .xls, .csv"
        ref="fileInput"
      >
      <button type="button" @click="$refs.fileInput.click()" class="upload-btn">
        Choose File
      </button>
      <i class="file-note">* Excel upload - Columns must be as follows: name, ic_number, mobile_number</i>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading">
      Loading...
    </div>

    <!-- Error Message -->
    <div v-if="error" class="error">
      {{ error }}
    </div>

    <!-- Data Table -->
    <div class="table-container table-responsive">
      <table id="tbl_non_existing_users" class="display table table-striped table-bordered">
        <thead>
          <tr>
            <th v-for="header in displayHeaders" :key="header">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="localTableData.length < 1">
            <td colspan="3" class="text-center">No Data.</td>
          </tr>
          <tr v-for="(row, index) in paginatedData" :key="index">
            <td v-for="header in tableHeaders" :key="header">
              {{ row[header] }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination Controls -->
      <div v-if="localTableData.length > recordsPerPage" class="pagination">
        <button type="button" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">Previous</button>

        <!-- Page numbers -->
        <button
          type="button"
          v-for="pageNum in visiblePageNumbers" 
          :key="pageNum" 
          @click="changePage(pageNum)" 
          :class="{ active: currentPage === pageNum }"
        >
          {{ pageNum }}
        </button>

        <button type="button" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx/xlsx.mjs';

export default {
  name: 'ExcelUploader',
  props: {
    tableData: {
      type: Array,
      required: false,
      default: () => [] // Changed to function to avoid sharing the same array across instances
    }
  },
  
  data() {
    return {
      tableHeaders: ['name', 'ic_number', 'mobile_number'],
      loading: false,
      error: null,
      localTableData: [], // Local copy of tableData
      currentPage: 1,      // Current page for pagination
      recordsPerPage: 20   // Number of records per page
    };
  },

  computed: {
    displayHeaders() {
      return this.tableHeaders.map((header) => {
        switch (header) {
          case 'name':
            return 'Name';
          case 'ic_number':
            return 'IC Number';
          case 'mobile_number':
            return 'Mobile Number';
          default:
            return header;
        }
      });
    },

    totalPages() {
      return Math.ceil(this.localTableData.length / this.recordsPerPage);
    },

    // Calculate the range of page numbers to show (max 5 pages)
    visiblePageNumbers() {
      const pages = [];
      const totalPages = this.totalPages;
      let startPage = Math.max(1, this.currentPage - 2); // Ensure we show at least 1 page
      let endPage = Math.min(startPage + 4, totalPages); // Ensure we don't exceed total pages

      // If the current page is too close to the end, adjust the range
      if (this.currentPage > totalPages - 2) {
        startPage = Math.max(1, totalPages - 4);
        endPage = totalPages;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.recordsPerPage;
      const end = start + this.recordsPerPage;
      return this.localTableData.slice(start, end);
    }
  },

  watch: {
    tableData: {
      immediate: true,
      handler(newValue) {
        this.localTableData = [...newValue];
      }
    }
  },

  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.loading = true;
      this.error = null;

      if (!file) {
        this.error = 'No file selected';
        this.loading = false;
        return;
      }

      const allowedExtensions = ['xlsx', 'xls', 'csv'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!allowedExtensions.includes(fileExtension)) {
        this.error = 'Invalid file type. Please upload an Excel or CSV file.';
        this.loading = false;
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const binaryString = e.target.result;
          const workbook = XLSX.read(binaryString, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: "A",
            raw: false
          });

          this.processExcelData(jsonData);
        } catch (error) {
          console.error('Processing error:', error);
          this.error = 'Error processing file: ' + error.message;
        } finally {
          this.loading = false;
        }
      };

      reader.onerror = () => {
        this.error = 'Error reading file';
        this.loading = false;
      };

      reader.readAsBinaryString(file);
    },

    processExcelData(jsonData) {
      if (!jsonData || jsonData.length === 0) {
        this.error = 'No data found in the file';
        return;
      }

      const headers = ['name', 'ic_number', 'mobile_number'];
      this.tableHeaders = headers;

      const dataRows = jsonData.slice(1);
      const processedData = dataRows.map(row => {
        const rowValues = Object.values(row);
        return {
          'name': rowValues[0],
          'ic_number': rowValues[1],
          'mobile_number': rowValues[2]
        };
      });

      // Update local data and emit to parent
      this.localTableData = processedData;
      this.$emit('update:tableData', processedData);
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    }
  }
};
</script>

<style scoped>
.excel-upload-container {
  padding: 20px;
}

.upload-section {
  margin-bottom: 20px;
}

.upload-section input[type="file"] {
  display: none;
}

.upload-btn {
  padding: 8px 16px;
  background-color: #AC8400;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
}

.upload-btn:hover {
  background-color: #AC8400;
}

.loading {
  margin: 10px 0;
  color: #666;
}

.error {
  color: red;
  margin: 10px 0;
}

.table-container {
  margin-top: 20px;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #ddd;
}

th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

th {
  background-color: #f4f4f4;
  font-weight: bold;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

tr:hover {
  background-color: #f5f5f5;
}

.pagination {
  margin-top: 10px;
  text-align: center;
}

.pagination button {
  padding: 5px 15px;
  margin: 0 5px;
  cursor: pointer;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.pagination button.active {
  background-color: #4CAF50;
  color: white;
}

.pagination button:hover:not(.active) {
  background-color: #f4f4f4;
}

.file-note {
  font-style: italic;

}
</style>
